import React, { useState } from "react";
import {
  Drawer,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Typography,
  IconButton,
  FormHelperText,
  InputLabel,
  FormControl
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axiosInstance from "../../api/axiosInstance";

/* ===== REACT QUILL TOOLBAR ===== */
const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"]
  ]
};

function ProductDrawer({
  open,
  onClose,
  onSubmit,
  productId,
  categories,
  brands,
  models,
  colors,   // ✅ ADD THIS

  form,
  setForm,
  loadBrands,
  loadModels
}) {
  const [errors, setErrors] = useState({});

  /* ================= VALIDATION ================= */
  const validate = () => {
    let err = {};
    if (!form.category?.length) err.category = "Category required";
    if (!form.title) err.title = "Title required";
    if (!form.sizes?.length) err.sizes = "Add at least one size";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ================= IMAGE REMOVE ================= */
  const removeExistingImage = (imgId) => {
    setForm({
      ...form,
      existingExtraImages: form.existingExtraImages.filter(
        (img) => img.id !== imgId
      ),
      deletedImages: [...(form.deletedImages || []), imgId]
    });
  };

  const removeExtraImage = (index) => {
    const updated = [...form.extraImages];
    updated.splice(index, 1);
    setForm({ ...form, extraImages: updated });
  };

  /* ================= SUBMIT ================= */
  const handleInternalSubmit = () => {
    if (!validate()) return;

    const data = new FormData();

    // data.append("category_id", form.category);
    data.append("product_title", form.title);
    data.append("product_details", form.details || "");
    data.append("sizes", JSON.stringify(form.sizes));

form.brand.forEach((id) => {
  data.append("brand_ids", id);
});

form.bikeModel.forEach((id) => {
  data.append("bike_model_ids", id);
});
    // if (form.bikeModel) data.append("bike_model_id", form.bikeModel);
    if (form.mainImage) data.append("product_img", form.mainImage);

    // delete images
    if (form.deletedImages?.length) {
      data.append("deleted_images", JSON.stringify(form.deletedImages));
    }
    form.category.forEach((catId) => {
      data.append("category_ids", catId);
    });
    // extra images
    form.extraImages.forEach((file) => {
      data.append("extra_images", file);
    });

    // colors
    form.selectedColors.forEach((colorId) => {
      data.append("color_ids", colorId);
    });

    onSubmit(data);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
    >
      <Box p={3} height="100%" overflow="auto">

        {/* HEADER */}
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography fontWeight={600} variant="h6">
            {productId ? "Edit Product" : "Add Product"}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* CATEGORY */}
<FormControl fullWidth error={!!errors.category}>
  <InputLabel>Category</InputLabel>

  <Select
    multiple
    value={form.category || []}
    label="Category"
    onChange={(e) => {
      const val = e.target.value;   // ✅ array
      setForm({
        ...form,
        category: val,
        brand: [],
        bikeModel: []
      });

      loadBrands(val);   // load brands for categories
    }}
    renderValue={(selected) =>
      categories
        .filter((c) => selected.includes(c.id))
        .map((c) => c.name)
        .join(", ")
    }
  >
    {categories.map((c) => (
      <MenuItem key={c.id} value={c.id}>
        {c.name}
      </MenuItem>
    ))}
  </Select>

</FormControl>
        {/* BRAND */}
<FormControl fullWidth sx={{ mt: 2 }}>
  <InputLabel>Brand</InputLabel>

<Select
  multiple
  value={form.brand || []}
  label="Brand"
  onChange={(e) => {
    const val = e.target.value;

    setForm({
      ...form,
      brand: val,
      bikeModel: []   // reset models
    });

    loadModels(val);   // send array
  }}
  renderValue={(selected) =>
    brands
      .filter((b) => selected.includes(b.id))
      .map((b) => b.name)
      .join(", ")
  }
>
    {[...new Set(brands.map(b => b.category.id))].map((catId) => {
  const category = brands.find(b => b.category.id === catId)?.category;

  return (
    <React.Fragment key={catId}>
      <MenuItem disabled>
        <b>{category?.name}</b>
      </MenuItem>

      {brands
        .filter((b) => b.category.id === catId)
        .map((b) => (
          <MenuItem key={b.id} value={b.id}>
            {b.name}
          </MenuItem>
        ))}
    </React.Fragment>
  );
})}
  </Select>
</FormControl>  
        <FormControl fullWidth sx={{ mt: 2 }}>
  <InputLabel>Model</InputLabel>

<Select
  multiple
  value={form.bikeModel || []}
  label="Model"
  onChange={(e) =>
    setForm({
      ...form,
      bikeModel: e.target.value
    })
  }
  renderValue={(selected) =>
    models
      .filter((m) => selected.includes(m.id))
      .map((m) => m.name)
      .join(", ")
  }
>
    {models.map((m) => (
      <MenuItem key={m.id} value={m.id}>
        {m.name}
      </MenuItem>
    ))}
  </Select>
</FormControl>

        {/* TITLE */}
        <TextField
          fullWidth
          sx={{ mt: 2 }}
          label="Title"
          value={form.title || ""}
          error={!!errors.title}
          helperText={errors.title}
          onChange={(e) =>
            setForm({ ...form, title: e.target.value })
          }
        />

        <Box mt={3}>
          <Typography variant="subtitle2">Sizes</Typography>

          {form.sizes.map((s, i) => (
            <Box key={i} display="flex" gap={1} mt={1}>
              <TextField
                label="Size"
                value={s.size}
                onChange={(e) => {
                  const updated = [...form.sizes];
                  updated[i].size = e.target.value;
                  setForm({ ...form, sizes: updated });
                }}
              />

              <TextField
                label="Price"
                // type="number"
                value={s.price}
                onChange={(e) => {
                  const updated = [...form.sizes];
                  updated[i].price = e.target.value;
                  setForm({ ...form, sizes: updated });
                }}
              />

              <Button
                color="error"
                onClick={() => {
                  const updated = form.sizes.filter((_, index) => index !== i);
                  setForm({ ...form, sizes: updated });
                }}
              >
                Delete
              </Button>
            </Box>
          ))}

          <Button
            sx={{ mt: 1 }}
            onClick={() =>
              setForm({
                ...form,
                sizes: [...form.sizes, { size: "", price: "" }]
              })
            }
          >
            + Add Size
          </Button>
        </Box>

        <Box mt={3}>
          <Typography variant="subtitle2" mb={1}>
            Product Details
          </Typography>

          <ReactQuill
            theme="snow"
            modules={quillModules}
            value={form.details || ""}
            onChange={(value) =>
              setForm({ ...form, details: value })
            }
            style={{ height: "180px", marginBottom: "80px" }}
          />
        </Box>

        {/* MAIN IMAGE */}
        <Box mt={3}>
          <Typography variant="subtitle2">Main Image</Typography>

          {(form.mainImage || form.existingMainImage) && (
            <Box mt={1}>
              <img
                src={
                  form.mainImage
                    ? URL.createObjectURL(form.mainImage)
                    : form.existingMainImage
                }
                alt="Main"
                width={120}
                height={120}
                style={{ objectFit: "cover", borderRadius: 8 }}
              />
            </Box>
          )}

          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              setForm({ ...form, mainImage: e.target.files[0] })
            }
          />
        </Box>
        {/* COLORS */}
        <Box mt={3}>
          <Typography variant="subtitle2">Colors</Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
            {colors.map((color) => {
              const selected = form.selectedColors.includes(color.id);

              return (
                <Box
                  key={color.id}
                  onClick={() => {
                    const updated = selected
                      ? form.selectedColors.filter((id) => id !== color.id)
                      : [...form.selectedColors, color.id];

                    setForm({ ...form, selectedColors: updated });
                  }}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: color.hex_code,
                    border: selected
                      ? "3px solid black"
                      : "1px solid #ccc",
                    cursor: "pointer"
                  }}
                />
              );
            })}
          </Box>
        </Box>
        {/* EXTRA IMAGES */}
        <Box mt={4}>
          <Typography variant="subtitle2">Extra Images</Typography>

          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) =>
              setForm({
                ...form,
                extraImages: [
                  ...form.extraImages,
                  ...Array.from(e.target.files)
                ]
              })
            }
          />

          <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
            {form.existingExtraImages?.map((img) => (
              <Box key={img.id} position="relative">
                <img
                  src={img.image}
                  width={80}
                  height={80}
                  style={{ objectFit: "cover", borderRadius: 6 }}
                  alt=""
                />
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    bgcolor: "#fff"
                  }}
                  onClick={() => removeExistingImage(img.id)}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ))}

            {form.extraImages.map((img, i) => (
              <Box key={i} position="relative">
                <img
                  src={URL.createObjectURL(img)}
                  width={80}
                  height={80}
                  style={{ objectFit: "cover", borderRadius: 6 }}
                  alt=""
                />
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    bgcolor: "#fff"
                  }}
                  onClick={() => removeExtraImage(i)}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>

        <Button
          fullWidth
          variant="contained"
          sx={{ mt: 4, mb: 3 }}
          onClick={handleInternalSubmit}
        >
          {productId ? "Update Product" : "Save Product"}
        </Button>

      </Box>
    </Drawer>
  );
}

export default ProductDrawer;
